import type {BoxProps} from '@miro-site/core'
import {Heading, Text, Link, Box} from '@miro-site/core'
import React from 'react'
import styled from 'styled-components'

export interface CategoryHeaderProps extends BoxProps {
  title: string
  viewAllLabel: string
  href?: React.AnchorHTMLAttributes<HTMLAnchorElement>['href']
}

export const CategoryLink = styled(Text).attrs(({theme}) => ({
  as: 'div',
  $variant: 'p1',
  $isDashed: false,
  $ml: [null, 'auto', 'auto', 'auto'],
  $nowrap: true,
  $styles: {
    fontWeight: 400,
    transition: `color ${theme.animation.transitionDuration}`,
    color: theme.templates.colors.secondary40,
  },
}))`
  &:after {
    content: ' \\2192';
  }
`

const CategoryHeaderLink = styled(Link).attrs({
  $styles: {
    display: 'flex',
    alignItems: ['flex-start', 'center', 'center', 'center'],
    flexDirection: ['column', 'row', 'row', 'row'],
  },
})`
  ${Heading} {
    transition: color ${({theme}) => theme.animation.transitionDuration};
  }

  &:hover {
    ${Heading}, ${CategoryLink} {
      color: ${({theme}) => theme.colors.primaryBlueHover};
    }
  }

  &:focus {
    ${({theme}) => theme.normalizeStyles({...theme.focus.outlines.withOffset})}
  }

  .focus-trap {
    width: 100%;
  }
`

const LinkWrapper = styled(Box)`
  display: flex;
  align-items: center;
  width: 100%;
`

const CategoryHeader = React.forwardRef<HTMLAnchorElement, CategoryHeaderProps>(
  ({title, viewAllLabel, ...props}, ref) => {
    return (
      <CategoryHeaderLink ref={ref} {...props}>
        <LinkWrapper>
          <Heading
            as="h2"
            $variant={['h3', 'h2', 'h2', 'h2']}
            $mb={[8, null, null, null]}
            $align="left"
            $styles={{
              maxWidth: [null, 645, 645, 645],
            }}
          >
            {title}
          </Heading>
          <CategoryLink>{viewAllLabel}</CategoryLink>
        </LinkWrapper>
      </CategoryHeaderLink>
    )
  },
)

CategoryHeader.displayName = 'CategoryHeader'

export default CategoryHeader
